import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";
import type PrescriptionJournalEvent from "./prescription-journal-event";

//---- Automatically generated, don't update this part ----

type FrequencyStringEnum =
  | "none"
  | "1-day"
  | "1-week"
  | "2-week"
  | "1-month"
  | "2-month"
  | "3-month"
  | "4-month"
  | "5-month"
  | "6-month"
  | "1-year";

export default class BioExamPrescribedJournalEvent
  extends JournalEvent
  implements PrescriptionJournalEvent
{
  static type = "bio-exam-prescribed-journal-event";

  /**
   * The list of bio exam orders
   */
  @attr({
    defaultValue() {
      return [];
    }
  })
  declare exams: string[];

  /**
   * The frequency of the current prescription
   * alert-type:
   */
  @attr("string", {
    defaultValue() {
      return "none";
    }
  })
  declare frequency: FrequencyStringEnum;

  /**
   * Some custom orders to add in the prescription
   */
  @attr("string") declare customOrders: string;

  /**
   * Validity period of the prescription, in month
   */
  @attr("number") declare validityPeriod: number;

  @attr("string") declare aldRelatedComments: string;

  /**
   * The rules should create an alert or not when the prescription expires soon
   */
  @attr("boolean", {
    allowNull: true
  })
  declare createExpirationAlert: boolean;

  static validations(intl: IntlService) {
    return {
      frequency: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.bio-exam-prescribed-journal-event.frequency"
          )
        }),
        validateInclusion({
          list: [
            "none",
            "1-day",
            "1-week",
            "2-week",
            "1-month",
            "2-month",
            "3-month",
            "4-month",
            "5-month",
            "6-month",
            "1-year"
          ],
          description: intl.t(
            "validations.bio-exam-prescribed-journal-event.frequency"
          )
        })
      ],
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.bio-exam-prescribed-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      validityPeriod: validateNumber({
        allowBlank: true,
        integer: true,
        description: intl.t(
          "validations.bio-exam-prescribed-journal-event.validity-period"
        )
      })
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "bioExamPrescribedJournalEvents"
  })
  declare patient: Patient;

  /**
   * @type {string}
   */
  @attr("string")
  uuid;

  get startDate(): Date {
    return this.occurredAt;
  }

  get endDate(): Date | undefined {
    if (!this.validityPeriod) {
      return;
    }
    const endDate = new Date(this.occurredAt);
    endDate.setMonth(endDate.getMonth() + this.validityPeriod);

    return endDate;
  }

  async renew(): Promise<BioExamPrescribedJournalEvent> {
    const prescriptionRenewed = await this.store
      .createRecord("bio-exam-prescribed-journal-event", {
        occurredAt: new Date(),
        patient: this.patient,
        validityPeriod: this.validityPeriod,
        exams: this.exams,
        frequency: this.frequency,
        customOrders: this.customOrders,
        aldRelatedComments: this.aldRelatedComments,
        comments: this.comments
      })
      .save();

    return prescriptionRenewed;
  }

  get pdfUrl() {
    return `/api/v1/bio_exam_prescriptions/${this.id}`;
  }
}

declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "bio-exam-prescribed-journal-event": BioExamPrescribedJournalEvent;
  }
}
